import LinkedTasksPanel from '../../components/work/v2/LinkedTasksPanel';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Tag,
  message,
  Avatar,
  Empty,
  List,
  Typography,
  Form,
  Input,
  Select,
  Spin,
  Modal,
  Space,
  Popconfirm,
} from 'antd';
import {
  ArrowRightOutlined,
  CustomerServiceOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { ticketsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { getImagePath } from '../../utils/imageHelper';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../hooks/useEntityBreadcrumb';
import EntityBreadcrumb from '../../components/common/EntityBreadcrumb';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const statusMeta = {
  open: { text: 'باز', color: 'red' },
  pending: { text: 'در انتظار', color: 'gold' },
  resolved: { text: 'حل‌شده', color: 'green' },
  closed: { text: 'بسته‌شده', color: 'blue' },
};

const priorityMeta = {
  low: { text: 'پایین', color: 'default' },
  medium: { text: 'متوسط', color: 'blue' },
  high: { text: 'بالا', color: 'orange' },
  urgent: { text: 'فوری', color: 'red' },
};

const priorityOptions = [
  { value: 'low', label: 'پایین' },
  { value: 'medium', label: 'متوسط' },
  { value: 'high', label: 'بالا' },
  { value: 'urgent', label: 'فوری' },
];

const channelName = (channels, id) => {
  if (id == null) return '—';
  const c = (channels || []).find((x) => String(x.id) === String(id));
  return c?.channel_name || '—';
};

const typeName = (types, id) => {
  if (id == null) return '—';
  const t = (types || []).find((x) => String(x.id) === String(id));
  return t?.type || '—';
};

const groupName = (groups, id) => {
  if (id == null) return '—';
  const g = (groups || []).find((x) => String(x.id) === String(id));
  return g?.group_name || '—';
};

const agentsForGroup = (groups, groupId) => {
  if (groupId == null || groupId === '') return [];
  const g = (groups || []).find((x) => String(x.id) === String(groupId));
  return (g?.enabled_agents || []).map((ea) => ({
    id: ea.agent_id ?? ea.user?.id,
    name: ea.user?.name,
  })).filter((a) => a.id != null);
};

const stripHtml = (html) => {
  if (html == null || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

/** پاسخ API می‌تواند reply را آرایه داخل ticket بفرستد؛ فرم ویرایش قبلاً فقط d.reply تک‌آیتمی را می‌خواند */
const initialDescriptionReply = (editPayload) => {
  const list = editPayload?.ticket?.reply ?? editPayload?.reply;
  const arr = Array.isArray(list) ? list : list ? [list] : [];
  const desc = arr.find((r) => Number(r?.is_description) === 1);
  return desc || arr[0] || null;
};

const tagLabelsFromTicket = (t) => {
  const raw = t?.tags ?? t?.ticket_tags ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => x?.tag_name ?? x?.value ?? (typeof x === 'string' ? x : null))
    .filter(Boolean);
};

const selectId = (v) => {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
};

const ReplyMessage = ({ html }) => {
  const isHtml = typeof html === 'string' && html.includes('<');
  if (isHtml) {
    return (
      <div
        className="ticket-reply-html text-gray-800 dark:text-gray-200 text-sm"
        style={{ fontFamily: 'CustomFont' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return (
    <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200 text-sm" style={{ fontFamily: 'CustomFont' }}>
      {html || '—'}
    </div>
  );
};

const ViewTicket = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { canEdit } = useResourcePermissions('tickets');
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const [detailsSubmitLoading, setDetailsSubmitLoading] = useState(false);
  const [otherSubmitLoading, setOtherSubmitLoading] = useState(false);
  const [composeSubmitting, setComposeSubmitting] = useState(false);
  const [replyActionId, setReplyActionId] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteReplyId, setNoteReplyId] = useState(null);
  const [noteEditLoading, setNoteEditLoading] = useState(false);
  const [statusQuickLoading, setStatusQuickLoading] = useState(false);
  const [detailsForm] = Form.useForm();
  const [otherForm] = Form.useForm();
  const [composeForm] = Form.useForm();
  const [noteForm] = Form.useForm();
  const otherGroupId = Form.useWatch('group_id', otherForm);

  const reloadTicket = useCallback(async () => {
    const showRes = await ticketsApi.getById(id);
    setPayload(showRes?.data || null);
  }, [id]);

  const reloadAll = useCallback(async () => {
    const [showRes, editRes] = await Promise.all([ticketsApi.getById(id), ticketsApi.getEditDetails(id)]);
    setPayload(showRes?.data || null);
    const d = editRes?.data;
    setEditDetails(d);
    if (d?.ticket) {
      const descReply = initialDescriptionReply(d);
      detailsForm.setFieldsValue({
        subject: d.ticket.subject,
        description: stripHtml(descReply?.message) || descReply?.message || '',
        ticket_reply_id: descReply?.id,
      });
      const t = d.ticket;
      otherForm.setFieldsValue({
        group_id: selectId(t.group_id),
        agent_id: selectId(t.agent_id),
        priority: t.priority,
        type_id: selectId(t.type_id),
        channel_id: selectId(t.channel_id),
        status: t.status,
        tag_labels: tagLabelsFromTicket(t),
      });
    }
  }, [id, detailsForm, otherForm]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await ticketsApi.getById(id);
        setPayload(res?.data || null);
      } catch (error) {
        message.error('خطا در دریافت تیکت');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!id || loading || !payload || !canEdit) return;
    let cancelled = false;
    (async () => {
      setEditDetailsLoading(true);
      try {
        const res = await ticketsApi.getEditDetails(id);
        if (cancelled) return;
        const d = res?.data;
        setEditDetails(d);
        if (d?.ticket) {
          const descReply = initialDescriptionReply(d);
          detailsForm.setFieldsValue({
            subject: d.ticket.subject,
            description: stripHtml(descReply?.message) || descReply?.message || '',
            ticket_reply_id: descReply?.id,
          });
          const t = d.ticket;
          otherForm.setFieldsValue({
            group_id: selectId(t.group_id),
            agent_id: selectId(t.agent_id),
            priority: t.priority,
            type_id: selectId(t.type_id),
            channel_id: selectId(t.channel_id),
            status: t.status,
            tag_labels: tagLabelsFromTicket(t),
          });
        }
      } catch (error) {
        if (!cancelled) message.error('خطا در دریافت اطلاعات ویرایش تیکت');
      } finally {
        if (!cancelled) setEditDetailsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loading, payload, canEdit, detailsForm, otherForm]);

  const groups = editDetails?.groups || payload?.groups || [];
  const types = editDetails?.types || payload?.types || [];
  const channels = editDetails?.channels || payload?.channels || [];

  const agentChoices = useMemo(
    () => agentsForGroup(groups, otherGroupId),
    [groups, otherGroupId]
  );

  useEffect(() => {
    if (otherGroupId == null) return;
    const allowed = new Set(agentChoices.map((a) => String(a.id)));
    const current = otherForm.getFieldValue('agent_id');
    if (current != null && !allowed.has(String(current))) {
      otherForm.setFieldsValue({ agent_id: undefined });
    }
  }, [otherGroupId, agentChoices, otherForm]);

  useEffect(() => {
    const t = payload?.ticket;
    if (!t) return;
    composeForm.setFieldsValue({
      status: t.status,
      type: 'reply',
      message: '',
      user_id: [],
    });
  }, [payload?.ticket?.id, payload?.ticket?.status, composeForm]);

  const ticket = payload?.ticket;
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/tickets',
    listLabel: 'تیکت‌ها',
    entityLabel: ticket?.subject || ticket?.ticket_number || id || 'تیکت',
  });

  const chart = payload?.ticketChart;
  const chartSummary = useMemo(() => {
    if (!chart?.labels?.length) return null;
    return chart.labels.map((label, i) => ({
      label,
      value: chart.values?.[i] ?? 0,
      color: chart.colors?.[i],
    }));
  }, [chart]);

  const onSaveDetails = async (values) => {
    setDetailsSubmitLoading(true);
    try {
      await ticketsApi.updateDetails(id, {
        subject: values.subject,
        description: values.description,
        ticket_reply_id: values.ticket_reply_id,
      });
      message.success('عنوان و توضیحات بروزرسانی شد');
      await reloadAll();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, detailsForm);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setDetailsSubmitLoading(false);
    }
  };

  const onSaveOther = async (values) => {
    // تبدیل به JSON string (مانند صفحه AddTicket)
    const tagsArray = (values.tag_labels || [])
      .filter(Boolean)
      .map((v) => ({ value: typeof v === 'string' ? v : String(v) }));
    
    // تبدیل آرایه به JSON string
    const tagsJsonString = JSON.stringify(tagsArray);
    
    setOtherSubmitLoading(true);
    try {
      await ticketsApi.updateOtherData(id, {
        group_id: values.group_id,
        agent_id: values.agent_id ?? null,
        priority: values.priority,
        type_id: values.type_id,
        channel_id: values.channel_id,
        status: values.status,
        tags: tagsJsonString,  // ارسال JSON string
      });
      message.success('اطلاعات تیکت بروزرسانی شد');
      await reloadAll();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, detailsForm);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setOtherSubmitLoading(false);
    }
  };

  const onSendMessage = async (values) => {
    setComposeSubmitting(true);
    try {
      await ticketsApi.addReplyOrNote(id, {
        status: values.status,
        type: values.type,
        message: values.message,
        user_id: values.user_id || [],
      });
      message.success(values.type === 'note' ? 'یادداشت ثبت شد' : 'پاسخ ارسال شد');
      composeForm.setFieldsValue({
        status: values.status,
        type: 'reply',
        message: '',
        user_id: [],
      });
      await reloadTicket();
      if (canEdit) {
        const editRes = await ticketsApi.getEditDetails(id);
        const d = editRes?.data;
        setEditDetails(d);
        if (d?.ticket) {
          const descReply = initialDescriptionReply(d);
          detailsForm.setFieldsValue({
            subject: d.ticket.subject,
            description: stripHtml(descReply?.message) || descReply?.message || '',
            ticket_reply_id: descReply?.id,
          });
        }
      }
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, detailsForm);
      else message.error(error?.message || 'خطا در ارسال');
    } finally {
      setComposeSubmitting(false);
    }
  };

  const onDeleteReply = async (replyId) => {
    setReplyActionId(replyId);
    try {
      await ticketsApi.deleteReply(replyId);
      message.success('حذف شد');
      await reloadTicket();
    } catch (error) {
      message.error('خطا در حذف');
    } finally {
      setReplyActionId(null);
    }
  };

  const openNoteEditor = (item) => {
    setNoteReplyId(item.id);
    noteForm.setFieldsValue({
      message: stripHtml(item.message) || item.message || '',
    });
    setNoteModalOpen(true);
  };

  const onSaveNoteEdit = async () => {
    const values = await noteForm.validateFields();
    setNoteEditLoading(true);
    try {
      await ticketsApi.editReplyNote({ id: noteReplyId, message: values.message });
      message.success('یادداشت بروزرسانی شد');
      setNoteModalOpen(false);
      setNoteReplyId(null);
      await reloadTicket();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, detailsForm);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setNoteEditLoading(false);
    }
  };

  const onQuickStatusChange = async (nextStatus) => {
    if (!ticket?.id) return;
    setStatusQuickLoading(true);
    try {
      await ticketsApi.changeStatus(ticket.id, nextStatus);
      message.success('وضعیت تیکت بروزرسانی شد');
      await reloadTicket();
      if (canEdit) {
        const editRes = await ticketsApi.getEditDetails(id);
        const d = editRes?.data;
        setEditDetails(d);
        if (d?.ticket) {
          otherForm.setFieldsValue({ status: d.ticket.status });
          composeForm.setFieldsValue({ status: d.ticket.status });
        }
      }
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    } finally {
      setStatusQuickLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (!ticket) {
    return (
      <MainLayout>
        <div className="p-6" style={{ fontFamily: 'CustomFont' }}>
          <Empty description="تیکت یافت نشد" />
          <Button
            className="mt-4"
            onClick={() => navigate(returnToRef.current || '/tickets', { state: buildGoBackState() })}
          >
            بازگشت به لیست
          </Button>
        </div>
      </MainLayout>
    );
  }

  const st = statusMeta[ticket.status] || { text: ticket.status || '—', color: 'default' };
  const pr = priorityMeta[ticket.priority] || { text: ticket.priority || '—', color: 'default' };
  const replies = [...(ticket.reply || [])].sort((a, b) => (a.id || 0) - (b.id || 0));

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <EntityBreadcrumb trail={trail} onNavigate={goToCrumb} />
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center flex-shrink-0">
                <CustomerServiceOutlined className="text-sky-600 dark:text-sky-400 text-xl" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">جزئیات تیکت</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">شماره {ticket.ticket_number}</p>
              </div>
              {!canEdit ? (
                <Tag className="flex-shrink-0">فقط مشاهده</Tag>
              ) : null}
            </div>
            <Space wrap>
              {canEdit ? (
                <Select
                  value={ticket.status}
                  onChange={onQuickStatusChange}
                  loading={statusQuickLoading}
                  style={{ minWidth: 150 }}
                  popupClassName="custom-font"
                >
                  {Object.keys(statusMeta).map((key) => (
                    <Option key={key} value={key}>
                      {statusMeta[key].text}
                    </Option>
                  ))}
                </Select>
              ) : null}
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(returnToRef.current || '/tickets', { state: buildGoBackState() })}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="text-xs text-gray-500 mb-1">وضعیت</div>
                <Tag color={st.color}>{st.text}</Tag>
              </Card>
              <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="text-xs text-gray-500 mb-1">اولویت</div>
                <Tag color={pr.color}>{pr.text}</Tag>
              </Card>
              <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="text-xs text-gray-500 mb-1">گروه</div>
                <div className="font-semibold">{groupName(groups, ticket.group_id)}</div>
              </Card>
              <Card className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="text-xs text-gray-500 mb-1">نوع</div>
                <div className="font-semibold">{typeName(types, ticket.type_id)}</div>
              </Card>
            </div>

            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="موضوع" span={2}>{ticket.subject}</Descriptions.Item>
                <Descriptions.Item label="کانال">{channelName(channels, ticket.channel_id)}</Descriptions.Item>
                <Descriptions.Item label="تاریخ ایجاد">{ticket.created_on || '—'}</Descriptions.Item>
                <Descriptions.Item label="پروژه">{ticket.project?.project_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="تاریخ بستن">{ticket.close_date || '—'}</Descriptions.Item>
              </Descriptions>
            </Card>

            {chartSummary?.length ? (
              <Card size="small" className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {chartSummary.map((row) => (
                    <div key={row.label} className="p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{row.label}</span>
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: row.color || '#ccc' }} />
                      </div>
                      <div className="text-xl font-bold mt-1">{row.value}</div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}

            {canEdit ? (
              <Card title="ارسال پاسخ یا یادداشت" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <Form form={composeForm} layout="vertical" onFinish={onSendMessage} className="max-w-3xl custom-font">
                  <Form.Item name="status" label="وضعیت تیکت">
                    <Select>
                      {Object.keys(statusMeta).map((key) => (
                        <Option key={key} value={key}>
                          {statusMeta[key].text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="type" label="نوع">
                    <Select>
                      <Option value="reply">پاسخ</Option>
                      <Option value="note">یادداشت داخلی</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="message" label="متن">
                    <TextArea rows={5} placeholder="متن پاسخ یا یادداشت..." />
                  </Form.Item>
                  <Form.Item name="user_id" label="اطلاع به کارمندان (اختیاری)">
                    <Select
                      mode="multiple"
                      placeholder="انتخاب کارمند"
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {(payload?.employees || []).map((e) => (
                        <Option key={e.id} value={e.id}>
                          {e.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={composeSubmitting}
                      icon={<SendOutlined />}
                      className={PANEL_PRIMARY_BTN_CLASS}
                    >
                      ارسال
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            ) : null}

            <Card title="گفتگو و یادداشت‌ها" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <List
                itemLayout="vertical"
                dataSource={replies}
                locale={{ emptyText: <Empty description="پاسخی ثبت نشده" /> }}
                renderItem={(item, idx) => {
                  const isNote = item.type === 'note';
                  return (
                    <List.Item key={item.id} className="!px-0 border-0">
                      <div className="flex gap-3 items-start">
                        <div className="relative flex flex-col items-center pt-1">
                          <Avatar src={getImagePath(item.user?.image_url)} size={38}>
                            {(item.user?.name || '?').charAt(0)}
                          </Avatar>
                          {idx !== replies.length - 1 ? (
                            <span className="block w-px h-14 bg-gray-200 dark:bg-gray-700 mt-2" />
                          ) : null}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Text strong>{item.user?.name || '—'}</Text>
                            <Tag color={isNote ? 'purple' : 'blue'}>{isNote ? 'یادداشت داخلی' : 'پاسخ'}</Tag>
                            {item.is_description ? <Tag color="gold">توضیح اولیه</Tag> : null}
                            <span className="text-xs text-gray-400">#{item.id}</span>
                          </div>

                          <div
                            className={`rounded-xl border p-3 sm:p-4 ${
                              isNote
                                ? 'bg-purple-50/60 dark:bg-purple-900/10 border-purple-100 dark:border-purple-800'
                                : 'bg-sky-50/60 dark:bg-sky-900/10 border-sky-100 dark:border-sky-800'
                            }`}
                          >
                            <ReplyMessage html={item.message} />
                          </div>

                          {canEdit ? (
                            <div className="flex justify-end mt-2">
                              <Space size="small">
                                {isNote ? (
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => openNoteEditor(item)}
                                    aria-label="ویرایش یادداشت"
                                  />
                                ) : null}
                                {!item.is_description ? (
                                  <Popconfirm
                                    title="حذف این مورد؟"
                                    okText="حذف"
                                    cancelText="لغو"
                                    onConfirm={() => onDeleteReply(item.id)}
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      loading={replyActionId === item.id}
                                      aria-label="حذف"
                                    />
                                  </Popconfirm>
                                ) : null}
                              </Space>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            </Card>

            <Card title="وظایف مرتبط" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <LinkedTasksPanel
                type="ticket"
                id={id}
                recordLabel={`#${ticket.ticket_number || id} ${ticket.subject || ''}`.trim()}
                returnTo={`/tickets/${id}`}
              />
            </Card>

            <Modal
              title="ویرایش یادداشت"
              open={noteModalOpen}
              onCancel={() => {
                setNoteModalOpen(false);
                setNoteReplyId(null);
              }}
              onOk={onSaveNoteEdit}
              confirmLoading={noteEditLoading}
              okText="ذخیره"
              cancelText="لغو"
              destroyOnClose
              okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
            >
              <Form form={noteForm} layout="vertical">
                <Form.Item name="message" label="متن یادداشت">
                  <TextArea rows={6} />
                </Form.Item>
              </Form>
            </Modal>

            {canEdit ? (
              <Card title="ویرایش عنوان و توضیحات" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                {editDetailsLoading || !editDetails?.ticket ? (
                  <div className="py-8 text-center">
                    <Spin />
                  </div>
                ) : (
                  <Form form={detailsForm} layout="vertical" onFinish={onSaveDetails} className="max-w-3xl custom-font">
                    <Form.Item name="ticket_reply_id" hidden>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name="subject" label="موضوع">
                      <Input />
                    </Form.Item>
                    <Form.Item name="description" label="توضیحات (متن اولیه)">
                      <TextArea rows={5} />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={detailsSubmitLoading}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ذخیره عنوان و توضیحات
                      </Button>
                    </Form.Item>
                  </Form>
                )}
              </Card>
            ) : null}

            {canEdit ? (
              <Card title="ویرایش سایر فیلدها" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                {editDetailsLoading || !editDetails?.ticket ? (
                  <div className="py-8 text-center">
                    <Spin />
                  </div>
                ) : (
                  <Form form={otherForm} layout="vertical" onFinish={onSaveOther} className="max-w-3xl custom-font">
                    <Form.Item name="group_id" label="گروه">
                      <Select placeholder="گروه">
                        {groups.map((g) => (
                          <Option key={g.id} value={g.id}>
                            {g.group_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="agent_id" label="پشتیبان">
                      <Select placeholder="انتخاب پشتیبان" allowClear>
                        {agentChoices.map((a) => (
                          <Option key={a.id} value={a.id}>
                            {a.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="priority" label="اولویت">
                      <Select>
                        {priorityOptions.map((p) => (
                          <Option key={p.value} value={p.value}>
                            {p.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="type_id" label="نوع">
                      <Select placeholder="نوع">
                        {types.map((t) => (
                          <Option key={t.id} value={t.id}>
                            {t.type}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="channel_id" label="کانال">
                      <Select placeholder="کانال">
                        {channels.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.channel_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="status" label="وضعیت">
                      <Select>
                        {Object.keys(statusMeta).map((key) => (
                          <Option key={key} value={key}>
                            {statusMeta[key].text}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="tag_labels" label="تگ‌ها">
                      <Select mode="tags" placeholder="تگ" tokenSeparators={[',']} />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={otherSubmitLoading}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ذخیره سایر فیلدها
                      </Button>
                    </Form.Item>
                  </Form>
                )}
              </Card>
            ) : null}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewTicket;
